import "@repo/utils/global/polyfills";

import {
  createEffect,
  FlowComponent,
  JSX,
  onCleanup,
  onMount,
  Show,
} from "solid-js";
import { createStore } from "solid-js/store";
import c from "class-c";
import { Motion, Presence } from "solid-motionone";
import { Toaster, ToastProvider } from "solid-notifications";
import overlayApi from "solid-overlays";

import { HTTPError } from "@repo/utils/api";
import FeatureStore from "@repo/utils-solid/FeatureStore";

import ContainerButton from "../buttons/ContainerButton";
import FeaturesProvider from "../context/FeaturesProvider";
import { ErrorProvider, useError } from "../error/ErrorProvider";
import Alert from "../info/Alert";

import { invalidate, mounted } from "./signals";

import "solid-overlays/index.css";
import "solid-notifications/index.css";
import "./GlobalRoot.scss";

FeatureStore.configure({
  cachePendingActions: true,
  useCaptureError: () => {
    const error = useError();
    return (e) => {
      if (!(typeof e === "string" || e instanceof Error))
        throw new Error(
          `Attempted to capture error with unsupported type: ${e}`,
        );
      return error.capture(e);
    };
  },
});
{
  const prefixes = {
    "m-": [],
    "mx-": ["m-"],
    "my-": ["m-"],
    "mt-": ["m-", "my-"],
    "mb-": ["m-", "my-"],
    "ml-": ["m-", "mx-"],
    "mr-": ["m-", "mx-"],
    "p-": [],
    "px-": ["p-"],
    "py-": ["p-"],
    "pt-": ["p-", "py-"],
    "pb-": ["p-", "py-"],
    "pl-": ["p-", "px-"],
    "pr-": ["p-", "px-"],
    "text:": [],
    "color:": [],
    "bg:": [],
    "z-": [],
    "position-": [],
  } as Record<string, string[]>;
  c.config.transform = (str) => {
    const encountered = new Set<string>();

    return str
      .split(" ")
      .reverse()
      .filter((className) => {
        const prefix = className.includes(":")
          ? `${className.split(":", 1)[0]}:`
          : `${className.split("-", 1)[0]}-`;

        if (prefix in prefixes) {
          if (className.startsWith("text:+")) {
            if (encountered.has("text:+")) return false;
            encountered.add("text:+");
            return true;
          }
          if (encountered.has(prefix)) return false;
          for (const p of prefixes[prefix]!) {
            if (encountered.has(p)) return false;
          }
          encountered.add(prefix);
        }
        return true;
      })
      .reverse()
      .join(" ");
  };
}

declare namespace GlobalRoot {
  type Props = {
    OverlaysProvider: ReturnType<
      ReturnType<typeof overlayApi<{}>>["create"]
    >["OverlaysProvider"];
    featureProviders: FlowComponent[];
    children: JSX.Element;
  };
}

function GlobalRoot(props: GlobalRoot.Props) {
  const [error, setError] = createStore({ message: "", show: false });

  onMount(mounted.notify);

  createEffect(() => {
    ({ ...error });

    const t = setTimeout(() => {
      setError("show", false);
    }, 5000);

    onCleanup(() => {
      clearTimeout(t);
    });
  });

  return (
    <div id="global-root">
      {(() => (
        invalidate.listen(),
        (
          <ErrorProvider
            onError={async (error) => {
              const message = await (async () => {
                if (typeof error === "string") return error;
                if (error instanceof HTTPError)
                  return (
                    (
                      await error.response.json().catch((e) => {
                        console.error(e);
                        return {};
                      })
                    ).message ||
                    `Request failed with a ${error.response.status} status code, please refresh or try again later.`
                  );
                if (error instanceof Error) return error.message;
                return "An unknown error occurred.";
              })();
              setError({ message, show: !!message });
            }}
          >
            <props.OverlaysProvider>
              {({ renderOverlays }) => (
                <ToastProvider theme="dark">
                  <FeaturesProvider featureProviders={props.featureProviders}>
                    <Toaster positionY="bottom" positionX="right" />
                    {props.children}
                    {renderOverlays()}
                  </FeaturesProvider>
                </ToastProvider>
              )}
            </props.OverlaysProvider>
            <Presence>
              <Show when={error.show}>
                <Motion.div
                  id="error"
                  class="center-child"
                  initial={{ opacity: 0, y: 24 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: 24 }}
                >
                  <ContainerButton
                    class="fit-content"
                    onClick={() => {
                      setError("show", false);
                    }}
                  >
                    <Alert type="error" class="p-2">
                      {error.message}
                    </Alert>
                  </ContainerButton>
                </Motion.div>
              </Show>
            </Presence>
          </ErrorProvider>
        )
      ))()}
    </div>
  );
}

GlobalRoot.ref = () => document.getElementById("global-root");

export default GlobalRoot;
